/*
 * *****************************************************************************
 *     Copyright (C)  Motorola Solutions, INC.
 *     All Rights Reserved.
 *     Motorola Solutions Confidential Restricted.
 * *****************************************************************************
 */

import { createFeatureSelector, createSelector } from '@ngrx/store';
import { mainFeature, MainState } from './main.reducer';
import {
    selectActiveCallId,
    selectObservedUser,
    selectOpenCalls,
    selectConnectionStatus,
    selectHasWebsocketConnection
} from '../../call/+state/call.selectors';
import {
    isQuiesced,
    selectCallListTabEnabled,
    selectHasInitialized,
    selectLoggedAtDifferentPosition,
    selectSupervisorTabEnabled
} from '../../configuration/+state/configuration.selectors';
import { selectHasUserMedia, selectIsPhoneRegistered } from '../../call/+state/media.selectors';
import {
    selectCCHubAttached,
    selectCCHubHeadsetConnected,
    selectCCHubInitialized,
    selectCCHubState,
    selectHeadsetDisconnectedAndTimedOut
} from '../../call/+state/cchub.selectors';
import { selectPosition } from '../../user/+state/user.selectors';
import { selectUsbAttached, selectUsbDisconnectedAndTimedOut, selectUsbFirmwareChecked, selectUsbHeadsetConnected } from '../../usb/+state/usb.selectors';

export interface ApplicationState {
    initialized: boolean;
    upgrading: boolean;
    position: number;
    media: boolean;
    websocket: boolean;
    registered: boolean;
    usbAttached: boolean;
    usbHeadsetConnected: boolean;
    usbDisconnectedAndTimedOut: boolean;
    usbFirmwareChecked: boolean;
    ccHubAttached: boolean;
    ccHubHeadsetConnected: boolean;
    ccHubDisconnectedAndTimedOut: boolean;
    ccHubInitialized: boolean;
    activeCall: boolean;
    isQuiesced: boolean;
    loggedAtDifferentPosition: { positionNumber: string; positionName: string };
}

export const selectMainState = createFeatureSelector<MainState>(mainFeature.name);

export const selectTabIndex = createSelector(selectMainState, (state) => state.tabIndex);
export const selectOpenTabs = createSelector(selectMainState, (state) => state.openTabs);

export const selectCurrentSelectedTab = createSelector(selectTabIndex, selectOpenTabs, (index, openTabs) => openTabs[index]);

export const selectEmptyObserverTabOpen = createSelector(selectObservedUser, (observedAgent) => Boolean(observedAgent));
export const selectMonitoringTabAvailable = createSelector(selectSupervisorTabEnabled, (supervisorViewEnabled) => !!supervisorViewEnabled);
export const selectMonitoringTabSelected = createSelector(
    selectMonitoringTabAvailable,
    selectCurrentSelectedTab,
    (tabAvailable, selectedTab) => !!tabAvailable && selectedTab === 'supervisor'
);

export const selectMakeCallTabShown = createSelector(
    selectMonitoringTabAvailable,
    selectCallListTabEnabled,
    (supervisor, dispatcher) => !supervisor && !dispatcher
);

export const selectComputeSelectedCallId = createSelector(
    selectCurrentSelectedTab,
    selectOpenCalls,
    (selectedTab, myOpenCalls) => myOpenCalls.find((call) => call.uuid === selectedTab)?.uuid
);

export const selectFirmwareUpgrading = createSelector(selectCCHubState, (state) => state.firmwareUpgrading);

export const selectApplicationState = createSelector(
    selectCCHubHeadsetConnected,
    selectPosition,
    selectHasUserMedia,
    selectHasWebsocketConnection(null, true),
    selectIsPhoneRegistered,
    selectCCHubAttached,
    selectCCHubInitialized,
    selectHeadsetDisconnectedAndTimedOut,
    selectHasInitialized,
    selectFirmwareUpgrading,
    selectConnectionStatus,
    selectActiveCallId,
    selectUsbAttached,
    selectUsbFirmwareChecked,
    selectUsbHeadsetConnected,
    selectUsbDisconnectedAndTimedOut,
    isQuiesced,
    selectLoggedAtDifferentPosition,
    (
        ccHubHeadsetConnected,
        position,
        media,
        websocket,
        registered,
        ccHubAttached,
        ccHubInitialized,
        ccHubDisconnectedAndTimedOut,
        initialized,
        upgrading,
        connectionState,
        activeCallId,
        usbAttached,
        usbFirmwareChecked,
        usbHeadsetConnected,
        usbDisconnectedAndTimedOut,
        isQuiesced,
        loggedAtDifferentPosition
    ) => {
        return {
            initialized,
            upgrading,
            position,
            media,
            websocket,
            registered,
            usbAttached,
            usbFirmwareChecked,
            usbHeadsetConnected,
            usbDisconnectedAndTimedOut,
            ccHubAttached,
            ccHubHeadsetConnected,
            ccHubDisconnectedAndTimedOut,
            ccHubInitialized,
            connectionState,
            activeCall: !!activeCallId,
            isQuiesced,
            loggedAtDifferentPosition
        } as ApplicationState;
    }
);

export const selectPreferredHomeTab = createSelector(selectMainState, (state) => state.preferredHomeTab);
